import * as React from 'react';
import { IButtonProps } from '@fluentui/react/lib/Button';
import { ITeachingBubbleStyles, TeachingBubble } from '@fluentui/react/lib/TeachingBubble';
import { useBoolean } from '@fluentui/react-hooks';
import { useContext, useState } from 'react';
import { INotificationBubblesProps } from 'src/notifications/models';
import { transformNotificationBubbles } from 'src/notifications/utils';
import useDismissNotification from 'src/notifications/hooks/useDismissNotification';
import { NotificationsContext } from 'src/notifications/context/NotificationsContext';
import DOMPurify from 'dompurify';

// TODO: Remove this mock data after generating documentation

// const legacyNotificationsMock: ILegacyNotification[] = [
//   {
//     id: 1,
//     type: 'TeachingBubble',
//     title: 'Introducing Our New Logo',
//     htmlMessage:
//       'We have updated our logo to better reflect our brand and identity. We hope you like it!',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:logo',
//     referenceLink: '',
//   },
//   {
//     id: 2,
//     type: 'TeachingBubble',
//     title: 'Enhanced Navigation Bar',
//     htmlMessage:
//       'Our side navigation bar has been added to streamline your workflow, allowing for quick and easy access to all sections of the app.',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:leftNavigation',
//     referenceLink: '',
//   },
//   {
//     id: 3,
//     type: 'TeachingBubble',
//     title: 'Updated Overview Page Layout',
//     htmlMessage:
//       'The Overview page has been restructured for improved clarity and efficiency, helping you get to the information that matters most, faster.',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:overview',
//     referenceLink: '',
//   },
//   {
//     id: 4,
//     type: 'TeachingBubble:RAIDAccess',
//     title: 'Introducing Risk, Action, Issue, and Dependency (RAID)',
//     htmlMessage:
//       'We have added a new feature to the app that allows you to manage risks, actions, issues, and dependencies in one place. Check it out today!',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:createAction',
//     referenceLink: 'click:createAction',
//   },
//   {
//     id: 5,
//     type: 'TeachingBubble:RAIDAccess',
//     title: 'You can create a Risk from here!',
//     htmlMessage:
//       'This option allows you to create a Risk from the Overview page. Click on the "Create Risk" button to get started.',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:riskMenu',
//     referenceLink: '',
//   },
//   {
//     id: 6,
//     type: 'TeachingBubble:RAIDAccess',
//     title: 'You can create a Issue from here!',
//     htmlMessage:
//       'This option allows you to create a Issue from the Overview page. Click on the "Create Issue" button to get started.',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:issueMenu',
//     referenceLink: '',
//   },
//   {
//     id: 7,
//     type: 'TeachingBubble:RAIDAccess',
//     title: 'You can create a Dependency from here!',
//     htmlMessage:
//       'This option allows you to create a Dependency from the Overview page. Click on the "Create Dependency" button to get started.',
//     startDate: '2021-01-01T00:00:00Z',
//     endDate: '2021-12-31T23:59:59Z',
//     downTimeStartDateTime: null,
//     downTimeEndDateTime: null,
//     isNotificationActive: true,
//     createdDate: null,
//     display: true,
//     category: 'TeachingBubble:home:dependencyMenu',
//     referenceLink: '',
//   },
// ];

export const NotificationBubbles: React.FunctionComponent<INotificationBubblesProps> = ({
  page,
  data
}) => {
  const [currentIndex, setCurrentIndex] = useState(0);
  const [teachingBubbleVisible, { toggle: toggleTeachingBubbleVisible }] = useBoolean(true);

  const { notificationConfig, legacyNotifications } = useContext(NotificationsContext);
  const bubbleContent = transformNotificationBubbles(
    legacyNotifications,
    page,
    notificationConfig.user?.flights || [],
    data
  );

  const { dismiss, isSubmitting } = useDismissNotification({
    apiUrl: notificationConfig.dismissApiUrl,
    authToken: `Bearer ${notificationConfig.apiToken}`,
    coreApiToken: notificationConfig.coreApiToken,
    graphToken: notificationConfig.graphToken,
    userId: notificationConfig.userEmail,
    handleDismiss: toggleTeachingBubbleVisible,
  });

  const handleDismiss = () => {
    dismiss(bubbleContent.map((content) => content.notificationId));
  };

  const handleClick = () => {
    const action = bubbleContent[currentIndex].action;
    if (action !== ('' || null)) {
      const [actionType, elementId] = action?.split(':');
      if (actionType === 'click') {
        clickAction(elementId);
      }
    }
    setCurrentIndex(currentIndex + 1);
  };

  const clickAction = (elementId: string) => {
    const element = document.getElementById(elementId);
    if (element) {
      element.click();
    }
  };

  const primaryButtonProps: IButtonProps = {
    children: currentIndex < bubbleContent.length - 1 ? 'Next' : 'Got it',
    onClick: currentIndex < bubbleContent.length - 1 ? handleClick : handleDismiss,
    disabled: currentIndex === bubbleContent.length - 1 && isSubmitting,
  };

  const secondaryButtonProps: IButtonProps =
    currentIndex > 0
      ? {
        children: 'Previous',
        onClick: () => setCurrentIndex(currentIndex - 1),
      }
      : {
        children: 'Later',
        onClick: toggleTeachingBubbleVisible,
      };
  const styles: Partial<ITeachingBubbleStyles> = {
    content: {
      display: "block"
    },
  };
  
  return (
    <>
      {teachingBubbleVisible && bubbleContent.length > 0 && (
        <TeachingBubble
          target={`#${bubbleContent[currentIndex].targetId}`}
          primaryButtonProps={primaryButtonProps}
          secondaryButtonProps={secondaryButtonProps}
          headline={bubbleContent[currentIndex].headline}
          onDismiss={toggleTeachingBubbleVisible}
          isWide={true}
          hasCondensedHeadline={true}
          hasCloseButton={true}
          styles={styles}
          footerContent={`${currentIndex + 1} of ${bubbleContent.length}`}
          calloutProps={{
            directionalHint: bubbleContent[currentIndex].bubblePosition,
            directionalHintFixed: false,
          }}>
          
          <div dangerouslySetInnerHTML={{ 
            __html: DOMPurify.sanitize(bubbleContent[currentIndex].content, {
              ADD_ATTR: ['target'],
              ALLOWED_ATTR: ['href', 'target', 'class', 'id', 'style']
            }) 
          }} />
        </TeachingBubble>
      )}
    </>
  );
};

// Generated by GitHub Copilot | Updated on: 2026-10-05 | 4 lines modified | Persist Got it dismissal safely
